/**
 * WsSubscriptionCenter — WebSocket 轮询订阅中心 [Claude Code]
 *
 * 集中管理 WS 轮询消息的订阅、ID 合并、发送和响应分发。
 * 每个 module（如 sourceStatus）只发送一次，多个订阅者的 IDs 自动合并。
 * 复用现有 SocketApi 的 stop + send 机制管理 interval 和重连。
 */
import $store from '@/store/index.js'

// 模块配置表：定义每种消息类型的参数结构 [Claude Code]
const MODULE_CONFIG = {
  sourceStatus: {
    wsName: 'pageSerBusRequestWs',
    paramKey: 'sourceObjectIds',
    matchKey: 'sourceObjectId',
    needUserId: true,
    extraParams: null
  },
  outputStatus: {
    wsName: 'pageSerBusRequestWs',
    paramKey: 'mediaIds',
    // 响应里实际返回的字段是 mediaId（和发送的 mediaIds 对应）[Claude Code]
    matchKey: 'mediaId',
    needUserId: false,
    extraParams: null
  },
  decoderDataV2: {
    wsName: 'pageSerBusRequestWs',
    paramKey: 'sourceObjectIds',
    // 响应数据结构为 { primary: { sourceObjectId, ... } }，需嵌套取值 [Claude Code]
    matchKey: (item) => item && item.primary && item.primary.sourceObjectId,
    needUserId: true,
    extraParams: null
  },
  projectStatus: {
    wsName: 'pageSerBusRequestWs',
    paramKey: 'businessIds',
    matchKey: 'businessId',
    needUserId: false,
    extraParams: { requireOutputStatus: true }
  }
}

class WsSubscriptionCenter {
  /**
   * @param {Object} store - Vuex store 实例 [Claude Code]
   * @param {Object} createWebsocket - $createWebsocket 插件实例 [Claude Code]
   */
  constructor(store, createWebsocket) {
    this.store = store || $store
    this.createWebsocket = createWebsocket
    // subscriptions: Map<module, Map<subscriberId, { ids, callback }>> [Claude Code]
    this.subscriptions = new Map()
  }

  /**
   * 获取 serverName（和现有组件 computed 保持一致）[Claude Code]
   */
  get serverName () {
    return this.store.state.initConfig.currentEnvAppName || 'mediahub'
  }

  /**
   * 注册订阅 [Claude Code]
   * @param {string} module - 消息类型 key
   * @param {string} subscriberId - 订阅者唯一标识（通常是组件 _uid）
   * @param {Array} ids - 订阅的 ID 列表
   * @param {Function} callback - 响应回调，接收过滤后的数据列表
   */
  subscribe (module, subscriberId, ids, callback) {
    if (!this.subscriptions.has(module)) {
      this.subscriptions.set(module, new Map())
    }
    const moduleSubs = this.subscriptions.get(module)
    moduleSubs.set(subscriberId, { ids: ids || [], callback })
    this._refreshModule(module)
  }

  /**
   * 更新订阅者的 IDs [Claude Code]
   * @param {string} module - 消息类型 key
   * @param {string} subscriberId - 订阅者唯一标识
   * @param {Array} newIds - 新的 ID 列表
   */
  updateIds (module, subscriberId, newIds) {
    const moduleSubs = this.subscriptions.get(module)
    if (!moduleSubs || !moduleSubs.has(subscriberId)) return
    const entry = moduleSubs.get(subscriberId)
    entry.ids = newIds || []
    this._refreshModule(module)
  }

  /**
   * 取消指定 module 的订阅 [Claude Code]
   * @param {string} module - 消息类型 key
   * @param {string} subscriberId - 订阅者唯一标识
   */
  unsubscribe (module, subscriberId) {
    const moduleSubs = this.subscriptions.get(module)
    if (!moduleSubs) return
    moduleSubs.delete(subscriberId)
    if (moduleSubs.size === 0) {
      this.subscriptions.delete(module)
      // 无订阅者，只 stop 不 send [Claude Code]
      const config = MODULE_CONFIG[module]
      if (config) {
        const ws = this._getWsInstance(config.wsName)
        if (ws) ws.stop(module)
      }
    } else {
      this._refreshModule(module)
    }
  }

  /**
   * 取消指定组件的所有 module 订阅（用于 beforeDestroy 清理）[Claude Code]
   * @param {string} subscriberId - 订阅者唯一标识
   */
  unsubscribeAll (subscriberId) {
    const modulesToRefresh = []
    this.subscriptions.forEach((moduleSubs, module) => {
      if (moduleSubs.has(subscriberId)) {
        moduleSubs.delete(subscriberId)
        if (moduleSubs.size === 0) {
          this.subscriptions.delete(module)
          const config = MODULE_CONFIG[module]
          if (config) {
            const ws = this._getWsInstance(config.wsName)
            if (ws) ws.stop(module)
          }
        } else {
          modulesToRefresh.push(module)
        }
      }
    })
    // 剩余有订阅者的 module 重新合并发送 [Claude Code]
    modulesToRefresh.forEach(module => this._refreshModule(module))
  }

  /**
   * 合并所有订阅者的 IDs（去重）[Claude Code]
   */
  _getMergedIds (module) {
    const moduleSubs = this.subscriptions.get(module)
    if (!moduleSubs || moduleSubs.size === 0) return []
    const idSet = new Set()
    moduleSubs.forEach(entry => {
      entry.ids.forEach(id => idSet.add(id))
    })
    return [...idSet]
  }

  /**
   * 获取 WS 实例 [Claude Code]
   */
  _getWsInstance (wsName) {
    return this.store.state.websocket[wsName]
  }

  /**
   * 确保 WS 连接存在，不存在则触发创建 [Claude Code]
   */
  _ensureConnection (wsName) {
    const ws = this._getWsInstance(wsName)
    if (ws && ws.socket) return true
    // 触发连接创建：pageSerBusRequestWs → pageSerBusRequestFn [Claude Code]
    const fnName = wsName.replace('Ws', 'Fn')
    if (this.createWebsocket && typeof this.createWebsocket[fnName] === 'function') {
      this.createWebsocket[fnName]()
    }
    return false
  }

  /**
   * 刷新模块：stop 旧的 + send 新合并的（和现有代码 stop→send 模式一致）[Claude Code]
   */
  _refreshModule (module) {
    const config = MODULE_CONFIG[module]
    if (!config) return
    const mergedIds = this._getMergedIds(module)
    const ws = this._getWsInstance(config.wsName)

    if (!ws) {
      this._ensureConnection(config.wsName)
      return
    }

    // 先 stop 再 send，和现有组件代码模式一致 [Claude Code]
    ws.stop(module)

    if (!mergedIds.length) return

    ws.send({
      message: this._buildMessage(module, mergedIds),
      time: 5000,
      key: module,
      immediate: true,
      serverName: this.serverName,
      success: res => {
        // 后端偶发返回非数组（null/对象），用 Array.isArray 校验避免 _dispatch 内 forEach 抛错 [Claude Code]
        const raw = res && res.result && res.result.result
        const list = Array.isArray(raw) ? raw : []
        this._dispatch(module, list)
      },
      error: () => {
        console.warn(`[WsSubscriptionCenter] ${module} 响应错误`)
      }
    })
  }

  /**
   * 构建嵌套 JSON.stringify 消息格式（保持现有协议不变）[Claude Code]
   */
  _buildMessage (module, mergedIds) {
    const config = MODULE_CONFIG[module]
    const innerData = { [config.paramKey]: mergedIds }
    if (config.needUserId) {
      innerData.userId = this.store.state.userInfo.id
    }
    if (config.extraParams) {
      Object.assign(innerData, config.extraParams)
    }
    return {
      module,
      data: JSON.stringify({
        module,
        data: JSON.stringify(innerData)
      }),
      serverName: this.serverName
    }
  }

  /**
   * 响应分发：按 matchKey 将数据回调给各订阅者 [Claude Code]
   */
  _dispatch (module, responseList) {
    const config = MODULE_CONFIG[module]
    const moduleSubs = this.subscriptions.get(module)
    if (!moduleSubs) return

    // matchKey 支持字符串（字段名）或函数（自定义取值，用于嵌套字段）[Claude Code]
    const getId = typeof config.matchKey === 'function'
      ? config.matchKey
      : (item) => item && item[config.matchKey]

    moduleSubs.forEach(entry => {
      const matched = responseList.filter(item =>
        entry.ids.includes(getId(item))
      )
      // 空数组也回调，让订阅者能感知到「id 全部下线」并清空 UI 状态 [Claude Code]
      try {
        entry.callback(matched)
      } catch (e) {
        console.error(`[WsSubscriptionCenter] ${module} callback 错误:`, e)
      }
    })
  }

  /**
   * 销毁中心：清除所有订阅 [Claude Code]
   */
  destroy () {
    this.subscriptions.forEach((moduleSubs, module) => {
      const config = MODULE_CONFIG[module]
      if (config) {
        const ws = this._getWsInstance(config.wsName)
        if (ws) ws.stop(module)
      }
    })
    this.subscriptions.clear()
  }
}

// 导出类和配置表（配置表导出用于测试和扩展）[Claude Code]
export { MODULE_CONFIG }
export default WsSubscriptionCenter
